import { z } from "zod";
import { smbHttpsUrlSchema } from "./smb-intake";

export const SMB_ENRICHMENT_STEPS = ["provision", "apply", "research", "services", "personas", "confidence"] as const;
export const smbEnrichmentStepSchema = z.enum(SMB_ENRICHMENT_STEPS);
export type SmbEnrichmentStep = z.infer<typeof smbEnrichmentStepSchema>;
export const smbEnrichmentFlagSchema = z.enum([
  "missing_primary_logo", "missing_services", "persona_configuration", "persona_failures",
  "low_confidence", "uncertain_request", "research_failed", "angle_failures",
]);
export type SmbEnrichmentFlag = z.infer<typeof smbEnrichmentFlagSchema>;
export const smbEnrichmentProgressSchema = z.object({
  completed: z.array(smbEnrichmentStepSchema).default([]),
  batchId: z.string().optional(),
  selectedPersonaIds: z.array(z.string()).max(3).default([]),
  productIds: z.array(z.string()).default([]),
  offerIds: z.array(z.string()).default([]),
  referenceImages: z.array(z.string()).default([]),
  logoUrls: z.array(z.string()).default([]),
  workPhotos: z.array(smbHttpsUrlSchema).max(12).optional(),
  operations: z.record(z.object({
    status: z.enum(["started", "completed"]),
    id: z.string().optional(),
  }).strict()).default({}),
  hold: z.object({
    step: smbEnrichmentStepSchema, flags: z.array(smbEnrichmentFlagSchema),
  }).strict().optional(),
  approvedFlags: z.array(smbEnrichmentFlagSchema).default([]),
}).strict();
export type SmbEnrichmentProgress = z.infer<typeof smbEnrichmentProgressSchema>;
export const smbApproveEnrichmentSchema = z.object({
  flags: z.array(smbEnrichmentFlagSchema).min(1).max(8),
}).strict();

/** Selection is ordered by the persisted starter job order, not completion time. */
export function selectSmbPersonas(
  jobs: readonly { personaId: string; sortOrder: number; status: string }[],
  frozen: readonly string[] = [],
): string[] {
  if (frozen.length) return [...frozen];
  const completed = [...jobs].filter(job => job.status === "completed")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.personaId.localeCompare(b.personaId));
  return completed.length >= 3 ? completed.slice(0, 3).map(job => job.personaId) : [];
}

export function scoreSmbConfidence(input: {
  hasClientLogo: boolean; serviceCount: number; selectedCount: number; researchConfidence: number | null;
}) {
  const research = Math.max(0, Math.min(1, (input.researchConfidence ?? 0) / 100));
  const score = Math.round(100 * (
    (input.hasClientLogo ? 0.2 : 0) + (input.serviceCount > 0 ? 0.25 : 0) +
    (input.selectedCount === 3 ? 0.35 : 0) + research * 0.2
  )) / 100;
  const flags: SmbEnrichmentFlag[] = [];
  if (!input.hasClientLogo) flags.push("missing_primary_logo");
  if (!input.serviceCount) flags.push("missing_services");
  if (input.selectedCount !== 3) flags.push("persona_failures");
  if (score < 0.6) flags.push("low_confidence");
  return { score, flags };
}
